import { useState, useEffect } from 'react';
import { detectUserLocation, isLocationInIndia, getDefaultMapCenter } from '../utils/locationUtils';
import { authenticatedFetch } from '../utils/auth';

const newDelhiCenter = {
  lat: 28.6139,
  lng: 77.2090
};

export const useLocationDetection = () => {
  const [userLocation, setUserLocation] = useState(null);
  const [isLocationInIndiaState, setIsLocationInIndiaState] = useState(true);
  const [locationLoading, setLocationLoading] = useState(true);
  const [locationSource, setLocationSource] = useState(null);
  const [showLocationMessage, setShowLocationMessage] = useState(false);
  const [defaultMapCenter, setDefaultMapCenter] = useState(newDelhiCenter);
  const [locationInitialized, setLocationInitialized] = useState(false);

  // Function to generate Digipin for user location
  const generateDigipinForUserLocation = async (location) => {
    if (!location || !location.lat || !location.lng) return location;
    
    // For default locations from My locations, use stored data instead of reverse geocoding
    if (location.source === 'my_locations_default') {
      // Use the data already provided from the My locations API
      return {
        ...location,
        address: location.address || 'Your Default Location',
        digipin: location.digipin || 'N/A'
      };
    }
    
    // For all other locations, use existing logic (unchanged)
    try {
      console.log('🔧 Calling generate-digipin API for location:', { lat: location.lat, lng: location.lng, source: location.source });
      const response = await authenticatedFetch('/generate-digipin', {
        method: 'POST',
        body: JSON.stringify({
          lat: location.lat,
          lng: location.lng,
          get_address: true
        }),
      });
      
      console.log('🔧 Generate-digipin API response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('🔧 Generate-digipin API response data:', data);
        return {
          ...location,
          digipin: data.digipin || 'N/A',
          address: data.address || 'Your Current Location'
        };
      } else {
        console.log('❌ Generate-digipin API failed with status:', response.status);
        // If API call fails, return location without digipin
        return {
          ...location,
          digipin: 'N/A',
          address: 'Your Current Location'
        };
      }
    } catch (error) {
      console.error('❌ Error generating digipin for user location:', error);
      // Return location without digipin if there's an error
      return {
        ...location,
        digipin: 'N/A',
        address: 'Your Current Location'
      };
    }
  };

  // Function to check geolocation permission using Permissions API
  const checkGeolocationPermission = async () => {
    try {
      // Check if Permissions API is supported
      if (!navigator.permissions) {
        console.log('❓ Permissions API not supported, showing location dialog');
        setShowLocationMessage(true);
        return;
      }

      // Query the geolocation permission status
      const permissionStatus = await navigator.permissions.query({ name: 'geolocation' });
      console.log('🔍 Current geolocation permission state:', permissionStatus.state);

      switch (permissionStatus.state) {
        case 'granted':
          console.log('✅ Geolocation permission already granted, getting location automatically');
          // Permission is granted, get location directly without showing dialog
          await handleLocationPermissionGranted();
          break;
          
        case 'denied':
          console.log('❌ Geolocation permission denied, using IP-based location');
          // Permission denied, fall back to IP location without showing dialog
          await handleLocationPermissionDenied();
          break;
          
        case 'prompt':
        default:
          console.log('❓ Geolocation permission not decided, showing location dialog');
          // Permission not yet decided, show the dialog
          if (!userLocation) {
            setShowLocationMessage(true);
          }
          break;
      }

      // Listen for permission changes
      permissionStatus.addEventListener('change', async () => {
        console.log('🔄 Geolocation permission changed to:', permissionStatus.state);
        
        // If permission was granted after being prompt/denied, get location
        if (permissionStatus.state === 'granted' && !userLocation) {
          console.log('✅ Permission granted, getting location automatically');
          setShowLocationMessage(false); // Hide dialog if it's showing
          await handleLocationPermissionGranted();
        }
        // If permission was denied after being granted, fall back to IP
        else if (permissionStatus.state === 'denied' && userLocation?.source === 'gps_location') {
          console.log('❌ Permission denied, falling back to IP location');
          await handleLocationPermissionDenied();
        }
      });
    } catch (error) {
      console.log('❌ Error checking geolocation permission:', error);
      // Fallback: show dialog if permission check fails
      if (!userLocation) {
        setShowLocationMessage(true);
      }
    }
  };

  const initializeLocation = async () => {
    // Prevent multiple initializations
    if (locationInitialized) {
      console.log('Location already initialized, skipping');
      return;
    }
    
    setLocationLoading(true);
    try {
      // First check if user has a default location set in "My locations"
      const token = localStorage.getItem('access_token');
      let defaultLocation = null;
      
      if (token) {
        try {
          const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/addresses/default`, {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });
          
          if (response.ok) {
            const data = await response.json();
            defaultLocation = data.default_address;
            console.log('Default location from My locations:', defaultLocation);
            console.log('Default location lat/lng check:', {
              hasLat: !!defaultLocation?.lat,
              hasLatitude: !!defaultLocation?.latitude,
              hasLng: !!defaultLocation?.lng,
              hasLongitude: !!defaultLocation?.longitude,
              latValue: defaultLocation?.lat || defaultLocation?.latitude,
              lngValue: defaultLocation?.lng || defaultLocation?.longitude
            });
          }
        } catch (error) {
          console.log('Error fetching default location from My locations, proceeding with normal detection:', error);
        }
      }
      
      // Check if user has set a default location in "My locations"
      if (defaultLocation && (defaultLocation.lat || defaultLocation.latitude) && (defaultLocation.lng || defaultLocation.longitude)) {
        console.log('User has default location set in My locations, using that:', defaultLocation.address);
        const defaultLocationData = {
          lat: parseFloat(defaultLocation.lat || defaultLocation.latitude),
          lng: parseFloat(defaultLocation.lng || defaultLocation.longitude),
          country_code: 'IN',
          country_name: 'India',
          source: 'my_locations_default',
          address: defaultLocation.address,
          digipin: defaultLocation.digipin || 'N/A'
        };
        
        // Generate Digipin for the default location (if not already available)
        const locationWithDigipin = await generateDigipinForUserLocation(defaultLocationData);
        
        setUserLocation(locationWithDigipin);
        setLocationSource(locationWithDigipin?.source);
        
        if (locationWithDigipin) {
          const inIndia = isLocationInIndia(locationWithDigipin);
          setIsLocationInIndiaState(inIndia);
          const center = getDefaultMapCenter(locationWithDigipin);
          setDefaultMapCenter(center);
        } else {
          setDefaultMapCenter(newDelhiCenter);
          setIsLocationInIndiaState(true);
        }
      } else {
        // No default location set in My locations, check geolocation permission
        console.log('No default location set in My locations, checking geolocation permission');
        await checkGeolocationPermission();
        setDefaultMapCenter(newDelhiCenter);
        setIsLocationInIndiaState(true);
      }
      
      // Mark location as initialized
      setLocationInitialized(true);
    } catch (error) {
      console.error('Location detection failed:', error);
      setDefaultMapCenter(newDelhiCenter);
      setIsLocationInIndiaState(true);
      setLocationInitialized(true);
    } finally {
      setLocationLoading(false);
    }
  };

  const refreshLocationDetection = async () => {
    setLocationLoading(true);
    try {
      const location = await detectUserLocation();
      
      // Generate Digipin for the detected location
      const locationWithDigipin = await generateDigipinForUserLocation(location);
      
      setUserLocation(locationWithDigipin);
      setLocationSource(locationWithDigipin?.source);
      
      if (locationWithDigipin) {
        const inIndia = isLocationInIndia(locationWithDigipin);
        setIsLocationInIndiaState(inIndia);
        const center = getDefaultMapCenter(locationWithDigipin);
        setDefaultMapCenter(center);
      } else {
        setDefaultMapCenter(newDelhiCenter);
        setIsLocationInIndiaState(true);
      }
    } catch (error) {
      console.error('Location detection failed during refresh:', error);
      setDefaultMapCenter(newDelhiCenter);
      setIsLocationInIndiaState(true);
    } finally {
      setLocationLoading(false);
    }
  };

  // Function to handle location permission granted
  const handleLocationPermissionGranted = async () => {
    console.log('✅ User clicked Allow Location - requesting GPS location');
    console.log('🔧 handleLocationPermissionGranted function called');
    
    // Hide the dialog immediately
    setShowLocationMessage(false);
    console.log('🔧 Dialog hidden, showLocationMessage set to false');
    
    if (!navigator.geolocation) {
      console.log('❌ Geolocation not supported');
      // Don't call handleLocationPermissionDenied as it might show the dialog again
      setLocationLoading(false);
      return;
    }
    
    // Check if we're on HTTPS (required for geolocation in most browsers)
    console.log('🔧 Current protocol:', window.location.protocol);
    console.log('🔧 Current hostname:', window.location.hostname);
    if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
      console.log('❌ Geolocation requires HTTPS (except localhost/127.0.0.1)');
      await handleLocationPermissionDenied();
      return;
    }
    
    setLocationLoading(true);
    
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          console.log('✅ GPS location obtained:', { lat: latitude, lng: longitude });
          
          const gpsLocation = {
            lat: latitude,
            lng: longitude,
            country_code: 'IN',
            country_name: 'India',
            source: 'gps_location'
          };
          
          // Generate Digipin for the GPS location
          const locationWithDigipin = await generateDigipinForUserLocation(gpsLocation);
          
          setUserLocation(locationWithDigipin);
          setLocationSource(locationWithDigipin?.source);
          
          if (locationWithDigipin) {
            const inIndia = isLocationInIndia(locationWithDigipin);
            setIsLocationInIndiaState(inIndia);
            const center = getDefaultMapCenter(locationWithDigipin);
            setDefaultMapCenter(center);
          } else {
            setDefaultMapCenter(newDelhiCenter);
            setIsLocationInIndiaState(true);
          }
          
          // Mark location as initialized to prevent dialog from showing again
          setLocationInitialized(true);
          console.log('✅ GPS location processing completed successfully');
        } catch (error) {
          console.log('❌ Error processing GPS location:', error);
          await handleLocationPermissionDenied();
        } finally {
          setLocationLoading(false);
        }
      },
      async (error) => {
        console.log('❌ GPS location failed:', error.message, 'Code:', error.code);
        // Fall back to IP-based location detection
        await handleLocationPermissionDenied();
        setLocationLoading(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 20000,
        maximumAge: 0
      }
    );
  };

  // Function to handle location permission denied
  const handleLocationPermissionDenied = async () => {
    console.log('❌ User denied location permission, using IP-based location');
    console.log('🔧 handleLocationPermissionDenied function called');
    setShowLocationMessage(false);
    
    setLocationLoading(true);
    try {
      // Use IP-based location detection with more reliable services
      const services = [
        'https://ipapi.co/json/',
        'https://ipinfo.io/json',
        'https://ip-api.com/json/',
        'https://ipwho.is/',
        'https://freeipapi.com/api/json',
        'https://ipapi.co/json/',
        'https://ip-api.com/json/',
        'https://ipapi.co/json/',
        'https://ipinfo.io/json'
      ];

      let ipLocation = null;
      for (const service of services) {
        try {
          console.log(`🌐 Trying IP service: ${service}`);
          
          // Create a timeout controller for better browser compatibility
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 5000);
          
          const response = await fetch(service, {
            method: 'GET',
            headers: { 'Accept': 'application/json' },
            signal: controller.signal
          });
          
          clearTimeout(timeoutId);

          if (response.ok) {
            const data = await response.json();
            console.log(`🌐 IP service response:`, data);
            
            let lat, lng, country;
            
            // Parse response based on service
            if (service.includes('ipapi.co')) {
              lat = data.latitude;
              lng = data.longitude;
              country = data.country_name;
            } else if (service.includes('ipinfo.io')) {
              const coords = data.loc?.split(',');
              lat = coords ? parseFloat(coords[0]) : null;
              lng = coords ? parseFloat(coords[1]) : null;
              country = data.country;
            } else if (service.includes('ip-api.com')) {
              lat = data.lat;
              lng = data.lon;
              country = data.country;
            } else if (service.includes('ipwho.is')) {
              lat = data.latitude;
              lng = data.longitude;
              country = data.country;
            } else if (service.includes('freeipapi.com')) {
              lat = data.latitude;
              lng = data.longitude;
              country = data.countryName;
            } else if (service.includes('ipgeolocation.io')) {
              lat = data.latitude;
              lng = data.longitude;
              country = data.country_name;
            } else if (service.includes('extreme-ip-lookup.com')) {
              lat = data.lat;
              lng = data.lon;
              country = data.country;
            }

            if (lat && lng && !isNaN(lat) && !isNaN(lng)) {
              console.log(`✅ IP location detected: ${lat}, ${lng} in ${country}`);
              ipLocation = {
                lat: parseFloat(lat),
                lng: parseFloat(lng),
                country_code: 'IN',
                country_name: country || 'India',
                source: 'ip_detection'
              };
              console.log(`✅ IP location object created:`, ipLocation);
              break;
            } else {
              console.log(`❌ Invalid coordinates from ${service}: lat=${lat} (type: ${typeof lat}), lng=${lng} (type: ${typeof lng})`);
            }
          }
        } catch (error) {
          console.log(`❌ IP service failed: ${service}`, error.message);
          continue;
        }
      }
      
      if (ipLocation) {
        console.log('✅ IP location found, processing...', ipLocation);
        const locationWithDigipin = await generateDigipinForUserLocation(ipLocation);
        console.log('✅ Location with Digipin generated:', locationWithDigipin);
        
        setUserLocation(locationWithDigipin);
        setLocationSource(locationWithDigipin?.source);
        
        if (locationWithDigipin) {
          const inIndia = isLocationInIndia(locationWithDigipin);
          setIsLocationInIndiaState(inIndia);
          const center = getDefaultMapCenter(locationWithDigipin);
          setDefaultMapCenter(center);
          console.log('✅ IP location set successfully, map center:', center);
        } else {
          setDefaultMapCenter(newDelhiCenter);
          setIsLocationInIndiaState(true);
          console.log('❌ Failed to generate Digipin for IP location, using default');
        }
        
        // Mark location as initialized
        setLocationInitialized(true);
      } else {
        // If all frontend IP services fail, try backend IP detection
        console.log('❌ All frontend IP services failed, trying backend IP detection');
        try {
          const backendResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/public/get-ip-location`);
          if (backendResponse.ok) {
            const backendData = await backendResponse.json();
            console.log('✅ Backend IP location response:', backendData);
            
            if (backendData.latitude && backendData.longitude) {
              const backendLocation = {
                lat: parseFloat(backendData.latitude),
                lng: parseFloat(backendData.longitude),
                country_code: backendData.country || 'IN',
                country_name: backendData.country_name || 'India',
                source: 'backend_ip_detection'
              };
              
              const locationWithDigipin = await generateDigipinForUserLocation(backendLocation);
              setUserLocation(locationWithDigipin);
              setLocationSource(locationWithDigipin?.source);
              
              if (locationWithDigipin) {
                const inIndia = isLocationInIndia(locationWithDigipin);
                setIsLocationInIndiaState(inIndia);
                const center = getDefaultMapCenter(locationWithDigipin);
                setDefaultMapCenter(center);
                console.log('✅ Backend IP location set successfully, map center:', center);
              } else {
                setDefaultMapCenter(newDelhiCenter);
                setIsLocationInIndiaState(true);
                console.log('❌ Failed to generate Digipin for backend IP location, using default');
              }
              
              setLocationInitialized(true);
            } else {
              throw new Error('Backend returned invalid coordinates');
            }
          } else {
            throw new Error(`Backend IP detection failed with status: ${backendResponse.status}`);
          }
        } catch (backendError) {
          console.log('❌ Backend IP detection also failed:', backendError.message);
          // If backend also fails, use default location (India center)
          console.log('❌ All IP detection methods failed, using default location (India)');
          const defaultLocation = {
            lat: 20.5937,
            lng: 78.9629,
            country_code: 'IN',
            country_name: 'India',
            source: 'default_location'
          };
          
          const locationWithDigipin = await generateDigipinForUserLocation(defaultLocation);
          setUserLocation(locationWithDigipin);
          setLocationSource(locationWithDigipin?.source);
          setDefaultMapCenter(newDelhiCenter);
          setIsLocationInIndiaState(true);
          setLocationInitialized(true);
        }
      }
    } catch (error) {
      console.error('❌ IP location detection failed:', error);
      setDefaultMapCenter(newDelhiCenter);
      setIsLocationInIndiaState(true);
      setLocationInitialized(true);
    } finally {
      setLocationLoading(false);
    }
  };

  // Function to redirect to My Location page
  const handleSetDefaultLocation = () => {
    console.log('🏠 User wants to set default location, redirecting to My Location page');
    console.log('🔧 handleSetDefaultLocation function called');
    setShowLocationMessage(false);
    // Redirect to My Location page
    window.location.href = '/my-locations';
  };

  useEffect(() => {
    initializeLocation();
  }, []);

  return {
    userLocation,
    isLocationInIndiaState,
    locationLoading,
    locationSource,
    showLocationMessage,
    defaultMapCenter,
    setDefaultMapCenter,
    initializeLocation,
    refreshLocationDetection,
    setShowLocationMessage,
    handleLocationPermissionGranted,
    handleLocationPermissionDenied,
    handleSetDefaultLocation
  };
};
